import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Loader2,
  Search,
  LayoutGrid,
  List,
  Star,
  BadgeCheck,
} from 'lucide-react'
import {
  browseApps,
  listFavoriteApps,
  listCategories,
  favoriteApp,
  unfavoriteApp,
  startApp,
  stopApp,
  rebuildApp,
  deleteApp,
  type App,
} from '../lib/api'
import { buildCategoryConfig, type CategoryStyle } from '../lib/categoryColors'
import { BrowseGridCard, BrowseListCard } from '../components/BrowseCard'
import { StyledSelect } from '../components/ui/StyledSelect'
import AppActions from '../components/AppActions'
import { AppContextMenu } from '../components/folders/AppContextMenu'
import { useFolders } from '../components/folders/useFolders'
import EditAppInfoModal from '../components/EditAppInfoModal'
import ShareModalComponent from '../components/ShareModal'
let categoryConfig: Record<string, CategoryStyle> = {}

/* ---- Main Component ---- */

export default function BrowseApps() {
  const [apps, setApps] = useState<App[]>([])
  const [editApp, setEditApp] = useState<App | null>(null)
  const [showShareApp, setShowShareApp] = useState<App | null>(null)
  const [favorites, setFavorites] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('browse-sort') || 'popular'
  })
  const [categoryFilter, setCategoryFilter] = useState(() => {
    return localStorage.getItem('browse-category') || 'all'
  })
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(() => localStorage.getItem('browse-favorites-only') === 'true')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    return (localStorage.getItem('browse-view') as 'grid' | 'list') || 'grid'
  })

  useEffect(() => {
    localStorage.setItem('browse-view', viewMode)
  }, [viewMode])

  useEffect(() => {
    localStorage.setItem('browse-sort', sortBy)
  }, [sortBy])

  useEffect(() => {
    localStorage.setItem('browse-category', categoryFilter)
  }, [categoryFilter])

  useEffect(() => {
    localStorage.setItem('browse-favorites-only', String(showFavoritesOnly))
  }, [showFavoritesOnly])

  // Folders for the context menu (reuses same folder store as My Apps)
  const {
    folders,
    addAppToFolder,
    removeAppFromFolder,
    getAppFolderIds,
    createFolderAndAddApp,
  } = useFolders(apps, 'my-apps')

  useEffect(() => {
    loadApps()
  }, [])

  async function loadApps() {
    try {
      const data = await browseApps()
      try {
        const cats = await listCategories()
        categoryConfig = buildCategoryConfig(cats)
      } catch {
        categoryConfig = {}
      }
      setApps(data)

      try {
        const favs = await listFavoriteApps()
        setFavorites(favs.map(f => f.name))
      } catch {
        // silently ignore
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load apps')
    } finally {
      setLoading(false)
    }
  }

  const toggleFavorite = useCallback(async (app: App) => {
    const wasFavorited = app.is_favorited
    const updateFav = (a: App) =>
      a.name === app.name ? { ...a, is_favorited: !wasFavorited } : a

    setApps(prev => prev.map(updateFav))
    if (wasFavorited) {
      setFavorites(prev => prev.filter(n => n !== app.name))
    } else {
      setFavorites(prev => [app.name, ...prev])
    }

    try {
      if (wasFavorited) {
        await unfavoriteApp(app.name)
      } else {
        await favoriteApp(app.name)
      }
    } catch {
      setApps(prev => prev.map(a =>
        a.name === app.name ? { ...a, is_favorited: wasFavorited } : a
      ))
      try {
        const favs = await listFavoriteApps()
        setFavorites(favs.map(f => f.name))
      } catch {
        // ignore
      }
    }
  }, [])

  const handleStart = useCallback(async (app: App) => {
    setApps(prev => prev.map(a => a.name === app.name ? { ...a, status: 'starting' } : a))
    try {
      await startApp(app.name)
      setApps(prev => prev.map(a => a.name === app.name ? { ...a, status: 'running' } : a))
    } catch {
      setApps(prev => prev.map(a => a.name === app.name ? { ...a, status: app.status } : a))
    }
  }, [])

  const handleStop = useCallback(async (app: App) => {
    setApps(prev => prev.map(a => a.name === app.name ? { ...a, status: 'stopped' } : a))
    try {
      await stopApp(app.name)
    } catch {
      setApps(prev => prev.map(a => a.name === app.name ? { ...a, status: app.status } : a))
    }
  }, [])

  const handleRebuild = useCallback(async (app: App) => {
    try {
      await rebuildApp(app.name)
      setApps(prev => prev.map(a => a.name === app.name ? { ...a, status: 'starting' } : a))
    } catch { /* ignore */ }
  }, [])

  const handleDelete = useCallback(async (app: App) => {
    try {
      await deleteApp(app.name)
      setApps(prev => prev.filter(a => a.name !== app.name))
    } catch { /* ignore */ }
  }, [])

  // Derive permission level for browse apps
  function getBrowsePermission(app: App): 'owner' | 'admin' | 'edit' | 'view' {
    const p = app.my_permission
    if (p === 'owner') return 'owner'
    if (p === 'admin') return 'admin'
    if (p === 'edit') return 'edit'
    return 'view'
  }

  // Build action callbacks based on permission (same as MyApps)
  function getActionProps(app: App) {
    const perm = getBrowsePermission(app)
    const canEdit = perm === 'owner' || perm === 'admin' || perm === 'edit'
    const isOwned = perm === 'owner'
    return {
      onStart: canEdit ? () => handleStart(app) : undefined,
      onStop: canEdit ? () => handleStop(app) : undefined,
      onRebuild: canEdit ? () => handleRebuild(app) : undefined,
      onRename: canEdit ? () => setEditApp(app) : undefined,
      onShare: isOwned ? () => setShowShareApp(app) : undefined,
      onDelete: isOwned ? () => handleDelete(app) : undefined,
    }
  }

  // Featured: admin-curated apps
  const [showAllFeatured, setShowAllFeatured] = useState(false)

  const featuredApps = useMemo(() => {
    return apps.filter(a => a.is_featured)
  }, [apps])

  const visibleFeaturedApps = showAllFeatured ? featuredApps : featuredApps.slice(0, 4)

  // Category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const app of apps) {
      if (app.category) {
        counts[app.category] = (counts[app.category] || 0) + 1
      }
    }
    return counts
  }, [apps])

  const favoriteSet = useMemo(() => new Set(favorites), [favorites])

  // Filtered apps for main grid
  const filteredApps = useMemo(() => {
    return apps
      .filter(app => {
        if (categoryFilter !== 'all' && app.category !== categoryFilter) return false
        if (showFavoritesOnly && !app.is_favorited && !favoriteSet.has(app.name)) return false
        if (search === '') return true
        const q = search.toLowerCase()
        return (
          (app.display_name || app.name).toLowerCase().includes(q) ||
          (app.description || '').toLowerCase().includes(q) ||
          (app.owner_name || app.owner_email || '').toLowerCase().includes(q)
        )
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return (a.display_name || a.name).localeCompare(b.display_name || b.name)
          case 'name-desc':
            return (b.display_name || b.name).localeCompare(a.display_name || a.name)
          case 'recent':
            return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime()
          case 'newest':
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          default: // 'popular'
            return (b.visit_count_30d || 0) - (a.visit_count_30d || 0)
        }
      })
  }, [apps, search, sortBy, categoryFilter, showFavoritesOnly, favoriteSet])

  const hasActiveFilters = search !== '' || categoryFilter !== 'all' || showFavoritesOnly

  function clearAllFilters() {
    setSearch('')
    setCategoryFilter('all')
    setShowFavoritesOnly(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Browse</h1>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 text-sm">
          {error}
        </div>
      )}

      {apps.length === 0 ? (
        <div className="text-center py-16 rounded-xl border border-dashed border-border">
          <Search className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">No apps available yet</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            When users share their apps with the platform, they&apos;ll appear here.
          </p>
        </div>
      ) : (
        <>
          {/* Control bar */}
          <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-border">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search apps..."
                className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>

            <StyledSelect
              value={sortBy}
              onChange={setSortBy}
              options={[
                { value: 'popular', label: 'Most Popular' },
                { value: 'recent', label: 'Recently Updated' },
                { value: 'newest', label: 'Newest First' },
                { value: 'name-asc', label: 'Name A → Z' },
                { value: 'name-desc', label: 'Name Z → A' },
              ]}
            />

            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={`inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-sm transition-colors border ${
                showFavoritesOnly
                  ? 'bg-yellow-500/15 border-yellow-500/30 text-yellow-600'
                  : 'border-border text-muted-foreground hover:border-yellow-500/30 hover:text-yellow-600'
              }`}
              title={showFavoritesOnly ? 'Show all' : 'Show favorites'}
            >
              <Star className="w-3.5 h-3.5" fill={showFavoritesOnly ? 'currentColor' : 'none'} />
              <span className="hidden sm:inline">Favorites</span>
            </button>

            <div className="flex items-center border border-border rounded-lg overflow-hidden ml-auto">
              
                <button title="Switch to grid layout"
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                </button>
              
              
                <button title="Switch to list layout"
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 transition-colors ${
                    viewMode === 'list'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <List className="w-3.5 h-3.5" />
                </button>
              
            </div>
          </div>

          {/* Category chips — color-coded */}
          <div className="flex flex-wrap items-center gap-1.5 mb-5">
            <button
              onClick={() => { setCategoryFilter('all'); setShowFavoritesOnly(false) }}
              className={`px-2.5 py-1 rounded border-l-2 text-xs transition-colors ${
                categoryFilter === 'all' && !showFavoritesOnly
                  ? 'bg-primary/10 border-l-primary text-primary font-medium'
                  : 'border border-border border-l-2 border-l-transparent text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}
            >
              All
            </button>
            {Object.entries(categoryConfig).map(([key, cfg]) => {
              const count = categoryCounts[key] || 0
              if (count === 0) return null
              return (
                <button
                  key={key}
                  onClick={() => setCategoryFilter(categoryFilter === key ? 'all' : key)}
                  className={`px-2.5 py-1 rounded border-l-2 text-xs transition-colors ${
                    categoryFilter === key
                      ? `${cfg.bg} ${cfg.border} ${cfg.text} font-medium`
                      : 'border border-border border-l-2 border-l-transparent text-muted-foreground hover:border-primary/30 hover:text-foreground'
                  }`}
                >
                  {cfg.label} ({count})
                </button>
              )
            })}
          </div>

          {/* Featured section */}
          {featuredApps.length > 0 && !hasActiveFilters && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold flex items-center gap-1.5">
                  <BadgeCheck className="w-5 h-5 text-amber-500" />
                  Featured
                </h2>
                {featuredApps.length > 4 && (
                  <button
                    onClick={() => setShowAllFeatured(!showAllFeatured)}
                    className="text-sm text-primary hover:underline"
                  >
                    {showAllFeatured ? 'Show less' : `See all ${featuredApps.length}`}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {visibleFeaturedApps.map((app) => {
                  const perm = getBrowsePermission(app)
                  const actions = getActionProps(app)
                  return (
                    <AppContextMenu
                      key={app.name}
                      app={app}
                      permission={perm}
                      folders={folders}
                      appFolderIds={getAppFolderIds(app.name)}
                      onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                      onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                      onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                      {...actions}
                    >
                      <BrowseGridCard
                        app={app}
                        catCfg={app.category ? categoryConfig[app.category] : null}
                        onToggleFavorite={toggleFavorite}
                        showStorageBadge={false}
                        menu={
                          <AppActions
                            app={app}
                            permission={perm}
                            folders={folders}
                            appFolderIds={getAppFolderIds(app.name)}
                            onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                            onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                            onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                            size="sm"
                            {...actions}
                          />
                        }
                      />
                    </AppContextMenu>
                  )
                })}
              </div>
            </div>
          )}

          {/* All Apps section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">All Apps</h2>
              <span className="text-sm text-muted-foreground">
                {filteredApps.length} {filteredApps.length === 1 ? 'item' : 'items'}
              </span>
            </div>

            {hasActiveFilters && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                <span>Showing {filteredApps.length} of {apps.length} apps</span>
                <button onClick={clearAllFilters} className="text-primary hover:underline">
                  Clear filters
                </button>
              </div>
            )}

            {filteredApps.length === 0 ? (
              <div className="text-center py-12 rounded-xl border border-dashed border-border">
                <Search className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                <h3 className="font-semibold mb-1">No apps match your filters</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Try a different search term or category.
                </p>
                <button onClick={clearAllFilters} className="text-primary hover:underline text-sm">
                  Clear all filters
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {filteredApps.map((app) => {
                  const perm = getBrowsePermission(app)
                  const actions = getActionProps(app)
                  return (
                    <AppContextMenu
                      key={app.name}
                      app={app}
                      permission={perm}
                      folders={folders}
                      appFolderIds={getAppFolderIds(app.name)}
                      onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                      onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                      onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                      {...actions}
                    >
                      <BrowseGridCard
                        app={app}
                        catCfg={app.category ? categoryConfig[app.category] : null}
                        onToggleFavorite={toggleFavorite}
                        showStorageBadge={false}
                        menu={
                          <AppActions
                            app={app}
                            permission={perm}
                            folders={folders}
                            appFolderIds={getAppFolderIds(app.name)}
                            onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                            onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                            onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                            size="sm"
                            {...actions}
                          />
                        }
                      />
                    </AppContextMenu>
                  )
                })}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredApps.map((app) => {
                  const perm = getBrowsePermission(app)
                  const actions = getActionProps(app)
                  return (
                    <AppContextMenu
                      key={app.name}
                      app={app}
                      permission={perm}
                      folders={folders}
                      appFolderIds={getAppFolderIds(app.name)}
                      onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                      onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                      onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                      {...actions}
                    >
                      <BrowseListCard
                        app={app}
                        catCfg={app.category ? categoryConfig[app.category] : null}
                        onToggleFavorite={toggleFavorite}
                        showStorageBadge={false}
                        menu={
                          <AppActions
                            app={app}
                            permission={perm}
                            folders={folders}
                            appFolderIds={getAppFolderIds(app.name)}
                            onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                            onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                            onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                            size="sm"
                            {...actions}
                          />
                        }
                      />
                    </AppContextMenu>
                  )
                })}
              </div>
            )}
          </div>
        </>
      )}

      {editApp && (
        <EditAppInfoModal
          app={editApp}
          onClose={() => setEditApp(null)}
          onSaved={(updated) => {
            setApps(prev => prev.map(a => a.id === editApp.id ? { ...a, ...updated } : a))
            setFavorites(prev => prev.map(name => name === editApp.name ? updated.name : name))
            setEditApp(null)
          }}
        />
      )}

      {showShareApp && (
        <ShareModalComponent
          app={showShareApp}
          onClose={() => setShowShareApp(null)}
          onShared={loadApps}
        />
      )}
    </div>
  )
}

/* ---- End of BrowseApps ---- */
